/**
 * Main Dashboard page with overview cards and statistics
 */

import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Avatar,
  LinearProgress,
  Chip,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  People as PeopleIcon,
  Campaign as CampaignIcon,
  Message as MessageIcon,
  MoreVert as MoreVertIcon,
  ArrowUpward as ArrowUpwardIcon,
  ArrowDownward as ArrowDownwardIcon,
} from '@mui/icons-material';

interface StatCardProps {
  title: string;
  value: string;
  change: number;
  icon: React.ReactNode;
  color: string;
  progress?: number;
}

const StatCard: React.FC<StatCardProps> = ({
  title,
  value,
  change,
  icon,
  color,
  progress,
}) => (
  <Card sx={{ height: '100%', position: 'relative' }}>
    <CardContent>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
        <Avatar
          sx={{
            bgcolor: `${color}.light`,
            color: `${color}.main`,
            width: 48,
            height: 48,
          }}
        >
          {icon}
        </Avatar>
        <IconButton size="small">
          <MoreVertIcon />
        </IconButton>
      </Box>
      
      <Typography variant="h4" component="div" sx={{ fontWeight: 600, mb: 1 }}>
        {value}
      </Typography>
      
      <Typography color="text.secondary" variant="body2" sx={{ mb: 2 }}>
        {title}
      </Typography>
      
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          {change >= 0 ? (
            <ArrowUpwardIcon sx={{ color: 'success.main', fontSize: 16, mr: 0.5 }} />
          ) : (
            <ArrowDownwardIcon sx={{ color: 'error.main', fontSize: 16, mr: 0.5 }} />
          )}
          <Typography
            variant="body2"
            sx={{
              color: change >= 0 ? 'success.main' : 'error.main',
              fontWeight: 500,
            }}
          >
            {Math.abs(change)}%
          </Typography>
        </Box>
        <Typography variant="caption" color="text.secondary">
          vs last month
        </Typography>
      </Box>
      
      {progress !== undefined && (
        <Box sx={{ mt: 2 }}>
          <LinearProgress
            variant="determinate"
            value={progress}
            sx={{
              height: 6,
              borderRadius: 3,
              bgcolor: `${color}.light`,
              '& .MuiLinearProgress-bar': {
                bgcolor: `${color}.main`,
              },
            }}
          />
        </Box>
      )}
    </CardContent>
  </Card>
);

interface ActivityItemProps {
  title: string;
  description: string;
  time: string;
  type: 'campaign' | 'message' | 'lead';
}

const ActivityItem: React.FC<ActivityItemProps> = ({ title, description, time, type }) => {
  const getIcon = () => {
    switch (type) {
      case 'campaign':
        return <CampaignIcon sx={{ color: 'primary.main' }} />;
      case 'message':
        return <MessageIcon sx={{ color: 'success.main' }} />;
      case 'lead':
        return <PeopleIcon sx={{ color: 'warning.main' }} />;
      default:
        return <CampaignIcon />;
    }
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-start', py: 1.5 }}>
      <Avatar
        sx={{
          width: 32,
          height: 32,
          bgcolor: 'grey.100',
          mr: 2,
        }}
      >
        {getIcon()}
      </Avatar>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
          {title}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
          {description}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {time}
        </Typography>
      </Box>
    </Box>
  );
};

export const Dashboard: React.FC = () => {
  const stats = [
    {
      title: 'Total Leads',
      value: '2,847',
      change: 12.5,
      icon: <PeopleIcon />,
      color: 'primary',
      progress: 75,
    },
    {
      title: 'Active Campaigns',
      value: '8',
      change: -2.1,
      icon: <CampaignIcon />,
      color: 'secondary',
    },
    {
      title: 'Messages Sent',
      value: '1,234',
      change: 8.7,
      icon: <MessageIcon />,
      color: 'success',
      progress: 60,
    },
    {
      title: 'Response Rate',
      value: '23.4%',
      change: 15.2,
      icon: <TrendingUpIcon />,
      color: 'warning',
      progress: 23.4,
    },
  ];

  const recentActivity = [
    {
      title: 'Campaign "Q4 Outreach" launched',
      description: 'Targeting 500 prospects in the tech industry',
      time: '2 hours ago',
      type: 'campaign' as const,
    },
    {
      title: 'New lead from LinkedIn',
      description: 'John Smith from TechCorp responded to your message',
      time: '4 hours ago',
      type: 'lead' as const,
    },
    {
      title: 'Message sent to prospect',
      description: 'Follow-up message sent to Sarah Johnson',
      time: '6 hours ago',
      type: 'message' as const,
    },
    {
      title: 'Campaign completed',
      description: 'Q3 Follow-up campaign finished with 89% delivery rate',
      time: '1 day ago',
      type: 'campaign' as const,
    },
  ];

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1" sx={{ fontWeight: 700, mb: 1 }}>
          Dashboard
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Welcome back! Here's what's happening with your campaigns today.
        </Typography>
      </Box>

      {/* Stats Cards */}
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: 3, mb: 4 }}>
        {stats.map((stat, index) => (
          <StatCard key={index} {...stat} />
        ))}
      </Box>

      {/* Content Grid */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '2fr 1fr' }, gap: 3 }}>
        {/* Recent Activity */}
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Recent Activity
              </Typography>
              <Chip label="View All" size="small" clickable />
            </Box>
            
            <Box>
              {recentActivity.map((activity, index) => (
                <ActivityItem key={index} {...activity} />
              ))}
            </Box>
          </CardContent>
        </Card>

        {/* Quick Actions */}
        <Card>
          <CardContent>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
              Quick Actions
            </Typography>
            
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Tooltip title="Create a new campaign">
                <Chip
                  icon={<CampaignIcon />}
                  label="New Campaign"
                  variant="outlined"
                  clickable
                  sx={{ justifyContent: 'flex-start', py: 1.5 }}
                />
              </Tooltip>
              
              <Tooltip title="Import leads from CSV">
                <Chip
                  icon={<PeopleIcon />}
                  label="Import Leads"
                  variant="outlined"
                  clickable
                  sx={{ justifyContent: 'flex-start', py: 1.5 }}
                />
              </Tooltip>
              
              <Tooltip title="Send a message">
                <Chip
                  icon={<MessageIcon />}
                  label="Send Message"
                  variant="outlined"
                  clickable
                  sx={{ justifyContent: 'flex-start', py: 1.5 }}
                />
              </Tooltip>
              
              <Tooltip title="View analytics">
                <Chip
                  icon={<TrendingUpIcon />}
                  label="View Analytics"
                  variant="outlined"
                  clickable
                  sx={{ justifyContent: 'flex-start', py: 1.5 }}
                />
              </Tooltip>
            </Box>
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
}; 